import { Grid, Typography, Stack, IconButton, Box, Chip } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as DeleteIcon } from 'common/assets/images/delete-icon.svg';
import { ReactComponent as DocTextIcon } from 'common/assets/images/document-img.svg';
import { Loader } from 'common/components/Loader/Loader';
import { NO_DATA_FOUND, TOAST_SEVERITY } from 'common/constants';
import {
	CHAT_RECORD_DELETED_SUCCESS_MESSAGE,
	CHAT_TYPE_OPTION,
	CHAT_USER_OPTIONS,
	RESOURCES_LABEL,
	THREAD_HIGHLIGHTS_LABEL,
} from 'common/constants/AskRiskLinkConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	dateTimeShortFormat,
	getFormattedSize,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import {
	deleteChatRecordById,
	getChatRecordById,
	getChatRecords,
} from '../api';
import {
	IChatRecords,
	IGPTMessageProps,
	IHighlight,
	IUploadedDocumnetInfo,
} from '../api/type-def';
import { formatMessageFromApi } from '../OpenRiskGPTUtil';

import RecordMessageView from './RecordMessageView';
import RemoveRecordModal from './RemoveRecordModal';

import style from './ChatRecords.module.scss';

interface IChatRecordsProps {
	handleToastChange: (message: string, severity: string) => void;
}

const ChatRecords = (props: IChatRecordsProps) => {
	const { handleToastChange } = props;
	const allThread = {
		highlight: 'All',
		message_id: 'all',
	};
	const [isRecordLoading, setIsRecordLoading] = useState<boolean>(false);
	const [records, setRecords] = useState<IChatRecords[]>([]);
	const [selectedThread, setSelectedThread] = useState<IHighlight>(allThread);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [selectedRecord, setSelectedRecord] = useState<IChatRecords>();
	const [recordMessage, setRecordMessage] = useState<IGPTMessageProps[]>([]);
	const [showRemoveRecordModal, setShowRemoveRecordModal] =
		useState<boolean>(false);
	const [deleteRecordId, setDeleteRecordId] = useState<string>('');
	const [isDeleting, setIsDeleting] = useState<boolean>(false);

	const dispatch = useDispatch();

	const { deleteIconFillColor, icon } = style;
	const { USER } = CHAT_USER_OPTIONS;
	const { DOC } = CHAT_TYPE_OPTION;
	const { OK } = HTTP_STATUS_CODES;
	const { ERROR, SUCCESS } = TOAST_SEVERITY;

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const getUserChatRecords = async () => {
		setIsRecordLoading(true);
		const resp = await getChatRecords();
		if (resp.responseBody && resp.statusCode === OK) {
			const data = resp.responseBody as IChatRecords[];
			data?.sort((a, b) => {
				const dateA: number = new Date(a.updatedAt).getTime();
				const dateB: number = new Date(b.updatedAt).getTime();
				return dateB - dateA;
			});
			setRecords(data);
			if (data[0]) {
				setSelectedRecord(data[0]);
				getRecordDetails(data[0].id);
			}
		} else {
			handleToastChange(APP_GENERAL_ERROR_MSG, ERROR);
		}
		setIsRecordLoading(false);
	};

	useEffect(() => {
		getUserChatRecords();
	}, []);

	const onRecordClickHandler = (e: IChatRecords) => {
		const recordId = e.id;
		setSelectedRecord(e);
		getRecordDetails(recordId);
		setSelectedThread(allThread);
	};

	const getRecordDetails = async (recordId: string) => {
		setIsLoading(true);
		const resp = await getChatRecordById(recordId);
		if (resp.responseBody && resp.statusCode === OK) {
			const data = resp.responseBody as IChatRecords;
			const formatedMessages = formatMessageFromApi(
				data.chatRecordMessages || [],
				loggedInUser,
			);
			setRecordMessage(formatedMessages);
		} else {
			handleToastChange(APP_GENERAL_ERROR_MSG, ERROR);
		}
		setIsLoading(false);
	};

	const onRecordDeleteHandler = (e: IChatRecords) => {
		setShowRemoveRecordModal(true);
		setDeleteRecordId(e.id);
	};

	const removeRecordModalCloseHandler = () => {
		setShowRemoveRecordModal(false);
	};

	const onDeleteConfirmation = async () => {
		setIsDeleting(true);
		const response = await deleteChatRecordById(deleteRecordId);
		if (response.statusCode === OK) {
			setIsDeleting(false);
			dispatch(
				setToastMessage({
					message: CHAT_RECORD_DELETED_SUCCESS_MESSAGE,
					severity: SUCCESS,
					isToastOpen: true,
				}),
			);
		} else {
			setIsDeleting(false);
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: ERROR,
					isToastOpen: true,
				}),
			);
		}
		removeRecordModalCloseHandler();
		getUserChatRecords();
	};

	const recordDisplay = (e: IChatRecords) => (
		<Stack
			direction="row"
			data-testid={e.id}
			className={
				e.id === selectedRecord?.id
					? style['record-card-selected']
					: style['record-card-normal']
			}
			onClick={() => onRecordClickHandler(e)}
		>
			<Box className={style['record-card-container']}>
				<Typography className={style['record-card-name']}>
					{e.recordName}
				</Typography>
				<Typography className={style['record-card-date']}>
					{dateTimeShortFormat(e.updatedAt)}
				</Typography>
			</Box>
			<IconButton
				className={style['delete-icon-wrapper']}
				data-testid="delete-action"
				disableRipple
				onClick={(event) => {
					onRecordDeleteHandler(e);
					event.stopPropagation();
				}}
			>
				<DeleteIcon
					data-testid="delete-icon"
					className={style['status-delete-stack']}
					fill={deleteIconFillColor}
				/>
			</IconButton>
		</Stack>
	);
	const onHighlightClick = (highlightObj: IHighlight) => {
		setSelectedThread(highlightObj);
		scrollToDiv(`${USER}-${highlightObj.message_id}`);
	};
	// Function to handle the scroll
	const scrollToDiv = (id: string) => {
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	};

	const documentRecord = recordMessage.find((ele) => ele.component === DOC);
	const documentEle: IUploadedDocumnetInfo[] = documentRecord?.documents || [];
	return (
		<Grid container>
			{records.length === 0 && !isRecordLoading && <div>{NO_DATA_FOUND}</div>}
			{records.length > 0 && (
				<>
					<Grid item sm={2} md={2} lg={3}>
						{records.map((e) => recordDisplay(e))}
					</Grid>
					<Grid item sm={10} md={10} lg={9} display="flex">
						<span className={style['vertical-line']} />
						<div className={style['message-container']}>
							{!isLoading && (
								<Grid container className={style['thread-container']}>
									<Grid item sm={12} className={style['thread-name']}>
										{THREAD_HIGHLIGHTS_LABEL}
									</Grid>
									<Grid item sm={12} className={style['thread-tags']}>
										<Chip
											id={`${USER}-all`}
											key="All"
											label="All"
											data-testid="chip-all"
											onClick={() =>
												onHighlightClick({
													highlight: 'All',
													message_id: 'all',
												})
											}
											color={
												selectedThread.message_id === 'all'
													? 'primary'
													: 'default'
											}
										/>
										{selectedRecord?.threadHighlights?.map(
											(highlightObj: IHighlight) => (
												<Chip
													data-testid={highlightObj.message_id}
													key={highlightObj.message_id}
													onClick={() => onHighlightClick(highlightObj)}
													label={toSentenceCase(highlightObj.highlight)}
													color={
														selectedThread.message_id ===
														highlightObj.message_id
															? 'primary'
															: 'default'
													}
												/>
											),
										)}
									</Grid>
								</Grid>
							)}
							<div className={style['record-chat-details']}>
								{isLoading && <Loader show={isLoading} />}
								{!isLoading &&
									recordMessage?.length > 0 &&
									recordMessage?.map((res) => (
										<RecordMessageView
											key={new Date()
												.getUTCMilliseconds()
												.toString(36)
												.substring(2, 15)}
											messageDetails={res}
										/>
									))}
							</div>
							{!isLoading && (
								<div className={style['user-doc-container']}>
									<Grid container className={style['resources-container']}>
										{documentEle?.length > 0 && (
											<Grid item sm={12} className={style['resources-name']}>
												{RESOURCES_LABEL}
											</Grid>
										)}
										{documentEle.length > 0 &&
											documentEle?.map((file) => (
												<Grid
													key={file?.id}
													item
													md={3.9}
													sm={5.8}
													lg={3.9}
													mr={1}
													className={style['upload-record-grid']}
												>
													<DocTextIcon className={icon} />
													<Typography className={style['file-name']}>
														{file?.name}
													</Typography>
													<Typography className={style['file-size']}>
														{getFormattedSize(file?.size)}
													</Typography>
												</Grid>
											))}
									</Grid>
								</div>
							)}
						</div>
					</Grid>
				</>
			)}
			{showRemoveRecordModal && (
				<RemoveRecordModal
					isModalOpen={showRemoveRecordModal}
					modalCloseHandler={removeRecordModalCloseHandler}
					isDeleting={isDeleting}
					onDeleteConfirmation={onDeleteConfirmation}
				/>
			)}
		</Grid>
	);
};

export default ChatRecords;
